import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, AlertCircle, CheckCircle, Info, Loader2, Plus, Minus } from 'lucide-react';
import { templateService } from '../../services/templateService';
import toast from 'react-hot-toast';

const TemplateModal = ({ isOpen, onClose, template = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [variables, setVariables] = useState([]);
  const [headerVariables, setHeaderVariables] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue
  } = useForm({
    defaultValues: {
      templateName: '',
      language: 'en_US',
      templateCategory: 'MARKETING',
      content: '',
      templateStatus: 'DRAFT',
      sampleText: '',
      headerText: '',
      headerSampleText: '',
      variableSamples: {},
      headerVariableSamples: {}
    }
  });

  const watchedContent = watch('content');
  const watchedName = watch('templateName');
  const watchedHeaderText = watch('headerText');
  
  // Check if content has variables
  const hasVariables = watchedContent && watchedContent.includes('{{');
  const hasHeaderVariables = watchedHeaderText && watchedHeaderText.includes('{{');

  // Extract variables from text
  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    
    const uniqueVars = [...new Set(matches.map(match => match.replace(/\{\{|\}\}/g, '')))];
    return uniqueVars.sort((a, b) => parseInt(a) - parseInt(b));
  };

  // Get next available variable number for body content
  const getNextBodyVariableNumber = () => {
    if (variables.length === 0) return 1;
    const maxVar = Math.max(...variables.map(v => parseInt(v)));
    return maxVar + 1;
  };

  // Get next available variable number for header
  const getNextHeaderVariableNumber = () => {
    if (headerVariables.length === 0) return 1;
    const maxVar = Math.max(...headerVariables.map(v => parseInt(v)));
    return maxVar + 1;
  };

  // Add variable to body content
  const addBodyVariable = () => {
    const nextVar = getNextBodyVariableNumber();
    const currentContent = watchedContent || '';
    const newContent = currentContent + `{{${nextVar}}}`;
    setValue('content', newContent);
  };

  // Add variable to header
  const addHeaderVariable = () => {
    const nextVar = getNextHeaderVariableNumber();
    const currentHeader = watchedHeaderText || '';
    const newHeader = currentHeader + `{{${nextVar}}}`;
    setValue('headerText', newHeader);
  };

  // Remove variable from body content
  const removeBodyVariable = (varNum) => {
    const currentContent = watchedContent || '';
    const newContent = currentContent.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), '');
    setValue('content', newContent);
  };

  // Remove variable from header
  const removeHeaderVariable = (varNum) => {
    const currentHeader = watchedHeaderText || '';
    const newHeader = currentHeader.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), '');
    setValue('headerText', newHeader);
  };

  // Update variables when content changes
  useEffect(() => {
    if (hasVariables) {
      const contentVars = extractVariables(watchedContent);
      setVariables(contentVars);
    } else {
      setVariables([]);
    }
  }, [watchedContent, hasVariables]);

  // Update header variables when header text changes
  useEffect(() => {
    if (hasHeaderVariables) {
      const headerVars = extractVariables(watchedHeaderText);
      setHeaderVariables(headerVars);
    } else {
      setHeaderVariables([]);
    }
  }, [watchedHeaderText, hasHeaderVariables]);

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (isOpen) {
      if (template) {
        setValue('templateName', template.templateName);
        setValue('language', template.language);
        setValue('templateCategory', template.templateCategory);
        setValue('content', template.content);
        setValue('templateStatus', template.templateStatus);
        setValue('sampleText', template.sampleText || '');
        setValue('headerText', template.headerText || '');
        setValue('headerSampleText', template.headerSampleText || '');
        
        // Parse variable samples if they exist
        if (template.variableSamples) {
          setValue('variableSamples', template.variableSamples);
        }
        if (template.headerVariableSamples) {
          setValue('headerVariableSamples', template.headerVariableSamples);
        }
      } else {
        reset();
        setVariables([]);
        setHeaderVariables([]);
      }
    }
  }, [isOpen, template, setValue, reset]);

  // Update preview when content changes
  useEffect(() => {
    setPreviewContent(watchedContent || '');
  }, [watchedContent]);

  // Check template name availability
  useEffect(() => {
    const checkName = async () => {
      if (watchedName && watchedName.length > 2) {
        // Don't check name if we're editing and the name hasn't changed
        if (template && watchedName === template.templateName) {
          setNameExists(false);
          return;
        }
        
        setIsCheckingName(true);
        try {
          const exists = await templateService.checkTemplateName(watchedName);
          setNameExists(exists);
        } catch (error) {
          console.error('Error checking template name:', error);
        } finally {
          setIsCheckingName(false);
        }
      } else {
        setNameExists(false);
      }
    };

    const timeoutId = setTimeout(checkName, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedName, template]);

  const onSubmit = async (data) => {
    if (nameExists && !template) {
      toast.error('Template name already exists');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare variable samples
      const variableSamples = {};
      variables.forEach(varNum => {
        const sampleValue = data[`variable_${varNum}`];
        if (sampleValue && sampleValue.trim()) {
          variableSamples[varNum] = sampleValue.trim();
        }
      });

      const headerVariableSamples = {};
      headerVariables.forEach(varNum => {
        const sampleValue = data[`headerVariable_${varNum}`];
        if (sampleValue && sampleValue.trim()) {
          headerVariableSamples[varNum] = sampleValue.trim();
        }
      });

      // Add variable samples to data
      data.variableSamples = Object.keys(variableSamples).length > 0 ? variableSamples : null;
      data.headerVariableSamples = Object.keys(headerVariableSamples).length > 0 ? headerVariableSamples : null;

      if (template) {
        await templateService.updateTemplate(template.templateId, data);
        toast.success('Template updated successfully');
      } else {
        await templateService.createTemplate(data);
        toast.success('Template created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.message || 'Failed to save template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form id="template-form" onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Template Name */}
          <div>
            <label className="block text-sm font-bold text-gray-800 mb-2">
              Template Name *
            </label>
            <div className="relative">
              <input
                {...register('templateName', {
                  required: 'Template name is required',
                  maxLength: {
                    value: 100,
                    message: 'Template name must not exceed 100 characters'
                  }
                })}
                type="text"
                className={`input-field ${errors.templateName ? 'border-red-500' : ''}`}
                placeholder="Enter template name (e.g., welcome_message)"
                disabled={isSubmitting}
              />
              {isCheckingName && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
              {watchedName && watchedName.length > 2 && !isCheckingName && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {nameExists ? (
                    <X className="h-4 w-4 text-red-500" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                </div>
              )}
            </div>
            {errors.templateName && (
              <p className="mt-1 text-sm text-red-600">{errors.templateName.message}</p>
            )}
            {watchedName && watchedName.length > 2 && !isCheckingName && nameExists && (
              <p className="mt-1 text-sm text-red-600">Template name already exists</p>
            )}
          </div>

          {/* Language and Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Language *
              </label>
              <select
                {...register('language', { required: 'Language is required' })}
                className={`input-field ${errors.language ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
              >
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="es_ES">Spanish (Spain)</option>
                <option value="es_MX">Spanish (Mexico)</option>
                <option value="fr_FR">French</option>
                <option value="de_DE">German</option>
                <option value="it_IT">Italian</option>
                <option value="pt_BR">Portuguese (Brazil)</option>
                <option value="hi_IN">Hindi</option>
                <option value="ar_SA">Arabic</option>
              </select>
              {errors.language && (
                <p className="mt-1 text-sm text-red-600">{errors.language.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Category *
              </label>
              <select
                {...register('templateCategory', { required: 'Category is required' })}
                className={`input-field ${errors.templateCategory ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
              >
                <option value="AUTHENTICATION">Authentication</option>
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
              </select>
              {errors.templateCategory && (
                <p className="mt-1 text-sm text-red-600">{errors.templateCategory.message}</p>
              )}
            </div>
          </div>

          {/* Template Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-gray-800">
                Template Content *
              </label>
              <button
                type="button"
                onClick={addBodyVariable}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Variable
              </button>
            </div>
            <textarea
              {...register('content', {
                required: 'Content is required',
                maxLength: {
                  value: 1000,
                  message: 'Content must not exceed 1000 characters'
                }
              })}
              rows={6}
              className={`input-field ${errors.content ? 'border-red-500' : ''}`}
              placeholder="Enter your message template here. Use {{1}}, {{2}}, etc. for dynamic content..."
              disabled={isSubmitting}
            />
            {errors.content && (
              <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
            )}
            <div className="mt-1 flex items-center justify-between text-sm text-gray-500">
              <span>{watchedContent?.length || 0}/1000 characters</span>
              {variables.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">Variables:</span>
                  <div className="flex flex-wrap gap-1">
                    {variables.map(varNum => (
                      <span
                        key={varNum}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-md"
                      >
                        {`{{${varNum}}}`}
                        <button
                          type="button"
                          onClick={() => removeBodyVariable(varNum)}
                          className="ml-1 text-indigo-500 hover:text-indigo-700"
                          disabled={isSubmitting}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Variable Samples for Body Content */}
          {hasVariables && variables.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">
                    Sample Text for Variables
                  </h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    Provide sample text for each variable in your template. This helps Meta understand how your template will look when used.
                  </p>
                  
                  <div className="space-y-3">
                    {variables.map(varNum => (
                      <div key={varNum}>
                        <label className="block text-sm font-bold text-gray-800 mb-1">
                          Sample for {`{{${varNum}}}`} *
                        </label>
                        <input
                          {...register(`variable_${varNum}`, {
                            required: `Sample text for variable {{${varNum}}} is required`,
                            maxLength: {
                              value: 100,
                              message: 'Sample text must not exceed 100 characters'
                            }
                          })}
                          type="text"
                          className={`input-field ${errors[`variable_${varNum}`] ? 'border-red-500' : ''}`}
                          placeholder={`Enter sample text for {{${varNum}}} (e.g., "John", "25%", "Welcome")`}
                          disabled={isSubmitting}
                        />
                        {errors[`variable_${varNum}`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`variable_${varNum}`].message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Header Text */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-gray-800">
                Header Text (Optional)
              </label>
              <button
                type="button"
                onClick={addHeaderVariable}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Variable
              </button>
            </div>
            <textarea
              {...register('headerText', {
                maxLength: {
                  value: 1000,
                  message: 'Header text must not exceed 1000 characters'
                }
              })}
              rows={3}
              className={`input-field ${errors.headerText ? 'border-red-500' : ''}`}
              placeholder="Enter header text here. Use {{1}}, {{2}}, etc. for dynamic content..."
              disabled={isSubmitting}
            />
            {errors.headerText && (
              <p className="mt-1 text-sm text-red-600">{errors.headerText.message}</p>
            )}
            <div className="mt-1 flex items-center justify-between text-sm text-gray-500">
              <span>{watchedHeaderText?.length || 0}/1000 characters</span>
              {headerVariables.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">Header Variables:</span>
                  <div className="flex flex-wrap gap-1">
                    {headerVariables.map(varNum => (
                      <span
                        key={varNum}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md"
                      >
                        {`{{${varNum}}}`}
                        <button
                          type="button"
                          onClick={() => removeHeaderVariable(varNum)}
                          className="ml-1 text-blue-500 hover:text-blue-700"
                          disabled={isSubmitting}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Variable Samples for Header */}
          {hasHeaderVariables && headerVariables.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                    Sample Text for Header Variables
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Provide sample text for each variable in your header.
                  </p>
                  
                  <div className="space-y-3">
                    {headerVariables.map(varNum => (
                      <div key={varNum}>
                        <label className="block text-sm font-bold text-gray-800 mb-1">
                          Sample for Header {`{{${varNum}}}`} *
                        </label>
                        <input
                          {...register(`headerVariable_${varNum}`, {
                            required: `Sample text for header variable {{${varNum}}} is required`,
                            maxLength: {
                              value: 100,
                              message: 'Sample text must not exceed 100 characters'
                            }
                          })}
                          type="text"
                          className={`input-field ${errors[`headerVariable_${varNum}`] ? 'border-red-500' : ''}`}
                          placeholder={`Enter sample text for header {{${varNum}}} (e.g., "John", "Welcome")`}
                          disabled={isSubmitting}
                        />
                        {errors[`headerVariable_${varNum}`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`headerVariable_${varNum}`].message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Guidelines */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-800 mb-2">Template Guidelines</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Use the "Add Variable" buttons to easily insert {`{{1}}`}, {`{{2}}`}, {`{{3}}`}, etc. for dynamic content</li>
              <li>• Variable numbers are automatically managed and will be sequential starting from 1</li>
              <li>• Click the minus (-) button on variable tags to remove them from your template</li>
              <li>• Provide sample text for each variable to help Meta understand your template</li>
              <li>• Keep template content clear and professional</li>
              <li>• Avoid using too many variables (recommended: 1-3 variables)</li>
            </ul>
          </div>

        </form>

        {/* Fixed Action Buttons Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="template-form"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            disabled={isSubmitting || (nameExists && !template)}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {template ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;